/**
 * Person Card Copy Functionality
 *
 * Handles copy-to-clipboard functionality for email addresses in person cards.
 * Supports multiple person cards on a single page by associating each copy button
 * with its preceding email link.
 */

class PersonCardCopy {
  constructor() {
    this.copyButtons = [];
    this.init();
  }

  init() {
    // Find all person cards and initialize copy functionality
    const personCards = document.querySelectorAll('.uom-c-person-card');

    personCards.forEach((card) => {
      this.initializeCard(card);
    });

    // Listen for dynamically added person cards
    this.observeNewCards();
  }

  initializeCard(card) {
    const copyButton = card.querySelector('[data-copy-text]');
    const emailLink = card.querySelector('.u-email');

    if (copyButton && emailLink) {
      // Store reference to the associated email for this button
      copyButton.emailElement = emailLink;

      // Add click event listener
      copyButton.addEventListener('click', this.handleCopyClick.bind(this));

      // Store button reference
      this.copyButtons.push(copyButton);

      // Add ARIA relationship
      if (!emailLink.id) {
        emailLink.id = `email-${Math.random().toString(36).substr(2, 9)}`;
      }
      copyButton.setAttribute('aria-describedby', emailLink.id);
    }
  }

  async handleCopyClick(event) {
    const button = event.currentTarget;
    const emailElement = button.emailElement;

    // Get email text from data attribute or from the email element
    const emailText =
      button.dataset.copyText || emailElement.textContent.trim();

    try {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(emailText);
        this.showCopyFeedback(button, 'success');
      } else {
        // Fallback for older browsers
        this.fallbackCopyToClipboard(emailText);
        this.showCopyFeedback(button, 'success');
      }
    } catch (error) {
      console.error('Failed to copy email:', error);
      this.showCopyFeedback(button, 'error');
    }
  }

  fallbackCopyToClipboard(text) {
    // Create temporary textarea for copying
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.top = '-9999px';

    document.body.appendChild(textarea);
    textarea.select();

    try {
      document.execCommand('copy');
    } finally {
      document.body.removeChild(textarea);
    }
  }

  showCopyFeedback(button, status) {
    // Store original content
    const originalHTML = button.innerHTML;
    const originalAriaLabel = button.getAttribute('aria-label');

    // Update button content based on status
    if (status === 'success') {
      button.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <polyline points="20,6 9,17 4,12"></polyline>
        </svg>
        <span class="visually-hidden">Copied!</span>
      `;
      button.setAttribute('aria-label', 'Email address copied to clipboard');
      button.classList.add('uom-c-person-card__copy-btn--success');
    } else {
      button.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="15" y1="9" x2="9" y2="15"></line>
          <line x1="9" y1="9" x2="15" y2="15"></line>
        </svg>
        <span class="visually-hidden">Copy failed</span>
      `;
      button.setAttribute('aria-label', 'Failed to copy email address');
      button.classList.add('uom-c-person-card__copy-btn--error');
    }

    // Reset after 2 seconds
    setTimeout(() => {
      button.innerHTML = originalHTML;
      button.setAttribute('aria-label', originalAriaLabel);
      button.classList.remove(
        'uom-c-person-card__copy-btn--success',
        'uom-c-person-card__copy-btn--error'
      );
    }, 2000);
  }

  observeNewCards() {
    // Watch for dynamically added person cards
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Check if the added node is a person card
            if (
              node.classList &&
              node.classList.contains('uom-c-person-card')
            ) {
              this.initializeCard(node);
            }

            // Check if the added node contains person cards
            const personCards =
              node.querySelectorAll &&
              node.querySelectorAll('.uom-c-person-card');
            if (personCards && personCards.length > 0) {
              personCards.forEach((card) => {
                this.initializeCard(card);
              });
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  // Public method to manually initialize a specific card
  initializeSpecificCard(cardElement) {
    if (cardElement && cardElement.classList.contains('uom-c-person-card')) {
      this.initializeCard(cardElement);
    }
  }

  // Public method to get copy status for testing
  getCopyButtons() {
    return this.copyButtons;
  }
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.personCardCopy = new PersonCardCopy();
  });
} else {
  window.personCardCopy = new PersonCardCopy();
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = PersonCardCopy;
}
